import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { API_URL } from "../config";

/**
 * Profile pictures for the manager employees table — fetched ON DEMAND.
 *
 * WHY THIS IS NOT A BATCH PREFETCH ANYMORE
 * ----------------------------------------
 * This hook used to eagerly fetch a picture for EVERY employee as soon as the
 * list loaded: one HTTP request per employee, 5 at a time, with a 100ms sleep
 * between batches. For a manager with 1,000 people in their hierarchy that is
 * 1,000 requests across 200 sequential batches.
 *
 * That did not just make this page slow — it froze the ENTIRE app, because
 * browsers allow only ~6 concurrent HTTP/1.1 connections PER ORIGIN. Holding
 * 5 of those 6 for minutes meant every other request to the API — navigating
 * to Attendance, loading the sidebar, anything — sat queued behind a thousand
 * avatar downloads. The UI was responsive; its network was starved.
 *
 * It also caused a render storm: two setLoadingPics() calls per picture is
 * ~2,000 state updates, and this table renders every row unpaginated, so each
 * update reconciled the whole list.
 *
 * THE RULES NOW (do not regress these):
 *   1. Nothing is fetched until a row asks for it (see EmployeeTable's
 *      IntersectionObserver — only rows actually on screen ask).
 *   2. MAX_CONCURRENT is 2, so at least 4 of the browser's ~6 connections
 *      per origin always remain free for navigation and everything else.
 *   3. In-flight/failed bookkeeping lives in refs, NOT state, so fetching a
 *      picture never re-renders the table. Results are committed in batches.
 *   4. Everything is aborted on unmount, so leaving the page frees the
 *      connections immediately instead of finishing orphaned downloads.
 */

// Leaves >=4 of the browser's ~6 per-origin connections free for the rest of
// the app. Raising this trades navigation responsiveness for avatar speed.
const MAX_CONCURRENT = 2;

// Results are flushed together on this cadence instead of one setState per
// picture, so N avatars cost ~N/flush re-renders instead of 2N.
const FLUSH_MS = 120;

export const useEmployeeProfilePics = ({ employees }) => {
  const [employeeProfilePics, setEmployeeProfilePics] = useState({});

  const cacheRef = useRef(new Map());      // id -> objectURL (successful)
  const failedRef = useRef(new Set());     // id -> never retry this session
  const inFlightRef = useRef(new Set());   // id -> request in progress
  const queueRef = useRef([]);             // ids waiting for a slot
  const activeRef = useRef(0);             // current in-flight count
  const pendingRef = useRef({});           // id -> url, awaiting flush
  const flushTimerRef = useRef(null);
  const abortRef = useRef(null);
  const mountedRef = useRef(true);

  // Self-healing signal.
  //
  // This MUST NOT be a one-shot controller created once. React StrictMode
  // (dev) mounts, unmounts and remounts, and the unmount cleanup aborts.
  // A single controller would stay aborted forever after that, so every
  // fetch would abort instantly and NO picture would ever load. Recreating
  // it whenever it is missing or already aborted makes remounts harmless.
  const getSignal = useCallback(() => {
    if (!abortRef.current || abortRef.current.signal.aborted) {
      abortRef.current = new AbortController();
    }
    return abortRef.current.signal;
  }, []);

  const flushSoon = useCallback(() => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      const batch = pendingRef.current;
      pendingRef.current = {};
      if (!mountedRef.current || Object.keys(batch).length === 0) return;
      setEmployeeProfilePics((prev) => ({ ...prev, ...batch }));
    }, FLUSH_MS);
  }, []);

  const pump = useCallback(() => {
    while (activeRef.current < MAX_CONCURRENT && queueRef.current.length > 0) {
      const id = queueRef.current.shift();
      if (!id || cacheRef.current.has(id) || failedRef.current.has(id)) continue;

      activeRef.current += 1;
      inFlightRef.current.add(id);

      (async () => {
        try {
          const res = await apiFetch(`${API_URL}/employees/pic/${id}`, {
            method: "GET",
            signal: getSignal(),
          });

          // 404 simply means "no picture" — remember it so we never ask again.
          if (res.status === 404 || !res.ok) {
            failedRef.current.add(id);
            return;
          }

          const blob = await res.blob();
          if (!blob || blob.size === 0) {
            failedRef.current.add(id);
            return;
          }

          const url = URL.createObjectURL(blob);
          if (!mountedRef.current) {
            // Unmounted mid-flight: don't leak the object URL.
            URL.revokeObjectURL(url);
            return;
          }
          cacheRef.current.set(id, url);
          pendingRef.current[id] = url;
          flushSoon();
        } catch (err) {
          if (err?.name !== "AbortError") failedRef.current.add(id);
        } finally {
          activeRef.current -= 1;
          inFlightRef.current.delete(id);
          if (mountedRef.current) pump();
        }
      })();
    }
  }, [flushSoon, getSignal]);

  /**
   * Ask for one employee's picture. Safe to call repeatedly and from render-
   * adjacent code: already-cached, already-failed, already-queued and
   * already-in-flight ids are all no-ops.
   */
  const requestPic = useCallback((id) => {
    if (!id) return;
    if (cacheRef.current.has(id)) return;
    if (failedRef.current.has(id)) return;
    if (inFlightRef.current.has(id)) return;
    if (queueRef.current.includes(id)) return;
    queueRef.current.push(id);
    pump();
  }, [pump]);

  // NOTE: deliberately NOT clearing the queue when `employees` changes.
  // That looked tidy but silently dropped every pending request whenever the
  // array identity changed, which can happen on any parent render. The
  // cache/failed/in-flight guards in requestPic already prevent duplicate
  // work, and a stale id at worst costs one small request.

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Free the connections immediately on navigate-away.
      abortRef.current?.abort();
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
      queueRef.current = [];
      activeRef.current = 0;
      inFlightRef.current.clear();
      failedRef.current.clear();
      pendingRef.current = {};
      // Revoking the object URLs means anything still holding them would show
      // a broken image, so clear the rendered map too. On a real unmount this
      // is a no-op; on a StrictMode remount it makes the hook refetch cleanly
      // instead of pointing at revoked URLs.
      cacheRef.current.forEach((url) => URL.revokeObjectURL(url));
      cacheRef.current.clear();
      setEmployeeProfilePics({});
    };
  }, []);

  return {
    employeeProfilePics,
    requestPic,
    // Kept for API compatibility with existing callers (modals). Now a plain
    // on-demand request rather than a batch participant.
    fetchEmployeeProfilePic: requestPic,
    loadingPics: inFlightRef.current,
  };
};
